import {createAction} from 'redux-actions'
import _ from "underscore"
import * as ActionTypes from './actionTypes'
import * as EntitiesActions from '../../entities/actions'
import {handleCommonTaskUpdateErrors, removeTasks, savePageSettings, setHasManageableTasks, setManagementAssignments,} from '../common/actions'
import TaskManagementService from "../TaskManagementService"
import {
    areProjectsFullyLoaded,
    getCollapsedProjects,
    getCollapsedProjectsWithIdsSet,
    getLoadableProjectPredicate,
    getProjectIds,
    getProjectLoadingPositions,
    getTaskCountsByWorkflowStatus,
    getTaskIdsOrder,
    isLoading,
    isMinimizedCards,
    thereAreNoTasks,
} from "./selectors"
import {changeAllTasksSelected as commonChangeAllTasksSelected} from '../taskSelection/actions'
import {getCustomer, getProject, getTask, getTasks as getAllTasksMap, getWorkflowStatus, getWorkflowStatusesArray} from "../../entities/selectors"
import messageResource from "../../messageResource"
import {arrayToSet, changeItemPosition, defer} from "../../atUtils"
import {
    getCustomFieldsFilters, getFilteredTaskStatusIds,
    getIntersectedCPSelection,
    getTaskNameFilter,
    getTaskStatusFilter,
    isShowOnlyManageableTasks,
    isViewerHasManageableTasks
} from "../common/selectors"
import {createActionObject} from "../../redux/utils"
import rpcCall from "../../util/rpc/rpcCalls"
import * as SyncCallbacks from '../syncCallbacks'
import {toast} from '../../toasts/actions'
import {isProjectFullyLoaded} from './utils'
import {changedTaskWorkflowStatus} from '../../entities/tasks/actions'
import {WorkflowStatusType} from '../../workflowStatus/constants'
import {isAborted} from '../../util/rpc/rpcUtils'

const taskManagementService = new TaskManagementService()
const LOAD_TASKS_LIMIT = 20

export const setInitialData = createAction(ActionTypes.INITIAL_DATA_SET)

export const addTasks = createAction(ActionTypes.TASKS_ADD)

export const addTasksOrder = createAction(ActionTypes.TASKS_ORDER_ADD)

export const prependTasksOrder = createAction(ActionTypes.TASKS_ORDER_PREPEND)

export const setTasksOrder = createAction(ActionTypes.TASKS_ORDER_SET)

export const addUpdatingTask = createAction(ActionTypes.UPDATING_TASK_ADD)

export const removeUpdatingTask = createAction(ActionTypes.UPDATING_TASK_REMOVE)

export const setProjectLoadingPosition = createAction(ActionTypes.PROJECT_LOADING_POSITION_SET)

export const resetProjectLoadingPositions = createAction(ActionTypes.PROJECT_LOADING_POSITIONS_RESET)

export const setLoadingInitialData = createAction(ActionTypes.LOADING_INITIAL_DATA_SET)

export const setLoadingProjectId = createAction(ActionTypes.LOADING_PROJECT_ID_SET)

export const setTasksCountByWorkflowStatuses = createAction(ActionTypes.TASKS_COUNT_BY_WORKFLOW_STATUS_SET)

export const setLoaded = createAction(ActionTypes.IS_LOADED_SET)

export const toggleKanbanCardsView = createAction(ActionTypes.MINIMIZED_CARDS_SET)

export const setProjectIdsContainingTasks = createAction(ActionTypes.PROJECTS_IDS_CONTAINING_TASK_ADD)

export const setNeedToLoadCounters = createAction(ActionTypes.NEED_TO_LOAD_COUNTERS_SET)

export const changeAllTasksSelectedAction = () => (dispatch, getState) => {
    // change only tasks in current projects exists. Optimization
    if (!thereAreNoTasks(getState())) {
        dispatch(commonChangeAllTasksSelected(arrayToSet(getTaskIdsOrder((getState())))))
    }
}

export const reloadKanban = () => (dispatch, getState) => {
    dispatch(setLoadingInitialData(true))
    const state = getState()
    const cpSelection = getIntersectedCPSelection(state)
    const workflowStatusFilter = getTaskStatusFilter(state)
    const filteredWorkflowStatusIds = getFilteredTaskStatusIds(state)
    const customFieldsFilters = getCustomFieldsFilters(state)
    const taskWorkflowStatuses = getWorkflowStatusesArray(state)
    const nameFilter = getTaskNameFilter(state)
    const onlyManageableTasks = isShowOnlyManageableTasks(state)

    taskManagementService
        .loadKanbanInitial(cpSelection, workflowStatusFilter, customFieldsFilters, taskWorkflowStatuses, nameFilter, onlyManageableTasks)
        .then(({
            taskCountByStatusId,
            workflowStatuses,
            customersProjectsInfo: {
                projectIdsContainingTasks,
                projects,
                customers,
            },
        }) => {
            const filteredTaskCountByStatusId = filteredWorkflowStatusIds.reduce((acc, curr) => ({
                ...acc,
               [curr]: taskCountByStatusId[curr]
            }), {})

            dispatch(EntitiesActions.addWorkflowStatuses(workflowStatuses))
            dispatch(EntitiesActions.addProjects(projects))
            dispatch(EntitiesActions.addCustomers(customers))


            dispatch(setInitialData({
                workflowStatusIds: Object.keys(filteredTaskCountByStatusId),
                taskCountsByWorkflowStatus: filteredTaskCountByStatusId,
                projectIds: projects.map(project => project.id),
            }))

            // place name filter applied to initial data and all tasks that will be loaded
            dispatch(createActionObject(ActionTypes.LOADED_TASKS_NAME_FILTER_SET, nameFilter))
            dispatch(setProjectIdsContainingTasks(projectIdsContainingTasks))
            dispatch(setTasksOrder([]))

            dispatch(setLoaded(true))
            dispatch(setLoadingInitialData(false))
        })
        .catch((e) => {
            if (isAborted(e)) return
            throw e
        })
}

export const reloadCounters = () => (dispatch, getState) => {
    const state = getState()
    const cpSelection = getIntersectedCPSelection(state)
    const workflowStatusFilter = getTaskStatusFilter(state)
    const filteredWorkflowStatusIds = getFilteredTaskStatusIds(state)
    const customFieldsFilters = getCustomFieldsFilters(state)
    const taskWorkflowStatuses = getWorkflowStatusesArray(state)
    const nameFilter = getTaskNameFilter(state)
    const onlyManageableTasks = isShowOnlyManageableTasks(state)

    taskManagementService
        .loadKanbanInitial(cpSelection, workflowStatusFilter, customFieldsFilters, taskWorkflowStatuses, nameFilter, onlyManageableTasks)
        .then(({
            taskCountByStatusId,
            workflowStatuses,
        }) => {
            const filteredTaskCountByStatusId = filteredWorkflowStatusIds.reduce((acc, curr) => ({
                ...acc,
                [curr]: taskCountByStatusId[curr]
            }), {})

            dispatch(EntitiesActions.addWorkflowStatuses(workflowStatuses))
            dispatch(setTasksCountByWorkflowStatuses(filteredTaskCountByStatusId))
            dispatch(setNeedToLoadCounters(false))
        })
}

export const loadMoreTasks = () => (dispatch, getState) => {
    const state = getState()
    const projectLoadingPositions = getProjectLoadingPositions(state)
    const isProjectLoadablePredicate = getLoadableProjectPredicate(state)

    const projectId = getProjectIds(state).find(id => {
        return isProjectLoadablePredicate(id) && !isProjectFullyLoaded(id, projectLoadingPositions)
    })

    // TODO may be need to replace with `getLoadedTasksNameFilter`
    const nameFilter = getTaskNameFilter(state)
    const onlyManageableTasks = isShowOnlyManageableTasks(state)
    const hasManageableTasks = isViewerHasManageableTasks(state)
    const intersectedCPSelection = getIntersectedCPSelection(state)
    const filteredWorkflowStatusIds = getFilteredTaskStatusIds(state)
    const customFieldsFilters = getCustomFieldsFilters(state)
    const taskWorkflowStatuses = getWorkflowStatusesArray(state)
    const loadingPosition = projectLoadingPositions[projectId]

    dispatch(setLoadingProjectId(projectId))

    taskManagementService
        .loadKanbanTasks(projectId, intersectedCPSelection, filteredWorkflowStatusIds, customFieldsFilters, taskWorkflowStatuses, nameFilter, onlyManageableTasks && hasManageableTasks,
        loadingPosition ? loadingPosition.count : 0, LOAD_TASKS_LIMIT)
        .then(data => {
            const tasks = data.tasks

            dispatch(setManagementAssignments(data["managementAssignments"]))
            dispatch(setHasManageableTasks(data["hasManageableTasks"]))

            dispatch(EntitiesActions.addTasks(tasks))
            dispatch(addTasksOrder(tasks.map(t => t.id)))

            const count = tasks.length > 0
                          ? _.max(Object.values(_.groupBy(tasks, 'workflowStatusId')).map(tasksByStatus => tasksByStatus.length))
                          : 0
            dispatch(setProjectLoadingPosition({
                id: projectId,
                count: loadingPosition ? loadingPosition.count + count : count,
                fullyLoaded: count < LOAD_TASKS_LIMIT
            }))
            dispatch(setLoadingProjectId(null))
        })
        .catch((err) => {
            setLoadingProjectId(null)
            if (isAborted(err)) {
                // it's okey
            } else {
                throw err
            }
        })
}

export const toggleProjectCollapsed = (projectId) => (dispatch, getState) => {
    const state = getState()
    const collapsedProjectsWithIdsSet = getCollapsedProjectsWithIdsSet(state)
    const collapsed = !(collapsedProjectsWithIdsSet.all || collapsedProjectsWithIdsSet.idsSet[projectId])
    if (collapsed)
        dispatch(collapseProject(projectId))
    else
        dispatch(expandProject(projectId))
}

export const changeCollapsedProjects = createAction(ActionTypes.COLLAPSED_PROJECTS_CHANGE)

export const collapseAllProjects = (isAllCollapsed) => (dispatch, getState) => {
    dispatch(changeCollapsedProjects({
        all: isAllCollapsed,
        ids: [],
    }))
    if (isAllCollapsed === false) {
        dispatch(handleTasksAndProjectsLoadingOrder(getProjectIds(getState()), []))
    }
}

export const expandProject = (id) => (dispatch, getState) => {
    const state = getState()
    const {all, ids} = getCollapsedProjects(state)
    const allProjectIds = getProjectIds(state)
    let newCollapsedIds = []
    if (all) {
        newCollapsedIds = allProjectIds.filter(sId => sId !== id)
    } else {
        newCollapsedIds = ids.filter(sId => sId !== id)
    }

    dispatch(changeCollapsedProjects({
        all: false,
        ids: newCollapsedIds,
    }))

    // reset loaded projects in case of expand
    if (all) {
        dispatch(handleTasksAndProjectsLoadingOrder(allProjectIds, []))
    } else {
        const projectIndex = allProjectIds.indexOf(id)
        // reset projects only if expanded project not fully loaded
        const fullyLoaded = isProjectFullyLoaded(id, getProjectLoadingPositions(state))
        if (projectIndex >= 0 && !fullyLoaded) {
            const projectIdsToReset = allProjectIds.slice(projectIndex + 1)

            const tasks = getAllTasksMap(state)
            const projectIdsToResetSet = arrayToSet(projectIdsToReset)
            const newTasksOrder = getTaskIdsOrder(state).filter((taskId) => {
                const task = tasks[taskId]
                return !projectIdsToResetSet[task.projectId]
            })
            dispatch(handleTasksAndProjectsLoadingOrder(projectIdsToReset, newTasksOrder))

            // init loading after reset prev projects
            dispatch(loadMoreTasks())
        }
    }
}

export const collapseProject = (id) => (dispatch, getState) => {
    const {all, ids} = getCollapsedProjects(getState())
    dispatch(changeCollapsedProjects({
        all: all,
        ids: ids.concat(id),
    }))
}

export const handleTasksAndProjectsLoadingOrder = (projectIdsToReset, newTasksOrder) => (dispatch) => {
    // don't change order of actions below
    dispatch(resetProjectLoadingPositions(projectIdsToReset))
    dispatch(setTasksOrder(newTasksOrder))
}

export const loadOnScroll = () => (dispatch, getState) => {
    const state = getState()
    if (!isLoading(state) && !areProjectsFullyLoaded(state)) {
        dispatch(loadMoreTasks())
    }
}

export const updateTaskStatusAndPosition = ({
    taskId,
    projectId,
    workflowStatusId,
    precedingTaskId,
    currentPrecedingTaskId,
    forceReopen = false
}) => (dispatch, getState) => {
    const state = getState()
    const task = getTask(state, taskId)
    const targetProject = getProject(state, projectId)
    const targetCustomer = getCustomer(state, targetProject.customerId)
    const workflowStatus = getWorkflowStatus(state, workflowStatusId)

    dispatch(addUpdatingTask(taskId))

    dispatch(EntitiesActions.addTask({
        ...task,
        workflowStatusId: workflowStatusId,
        projectId: projectId,
        open: workflowStatus.type !== WorkflowStatusType.COMPLETED
    }))

    dispatch(setTasksOrder(changeItemPosition(getTaskIdsOrder(state), taskId, precedingTaskId)))

    const method = projectId === task.projectId ? 'TaskManagementService.updateTaskWorkflowStatusAndPosition' : 'TaskManagementService.moveTask'

    rpcCall(
        method,
        [task.id, projectId, workflowStatus.type, workflowStatus.id, workflowStatus.name, precedingTaskId, forceReopen],
        (error) => {
            if (error.getTextStatus() === "rpc_customException") {
                const exception = error.getErrorThrown().exception
                switch (exception) {
                    case 'com.actimind.actitime.task.InsufficientRightsToEditTaskStatus':
                        alert(messageResource.getMessage("change_tasks_workflow_status.insufficient_rights_to_edit_task_status"))
                        window.location.reload()
                        return true
                    case 'com.actimind.actitime.task.ProjectIsArchivedException':
                        if (confirm(messageResource.getMessage('task.error.reopen.project_archived'))) {
                            dispatch(EntitiesActions.addTask({
                                ...task,
                            }))
                            dispatch(
                                updateTaskStatusAndPosition({taskId, projectId, workflowStatusId, precedingTaskId, currentPrecedingTaskId, forceReopen: true}))
                            error.handle()
                        }
                        return true
                    case "com.actimind.actitime.services.WorkflowStatusesFeatureBecameDisabledException":
                        alert(messageResource.getMessage("change_tasks_workflow_status.feature_became_disabled"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.services.NoRightToEditHierarchyException":
                        alert(messageResource.getMessage("task.error.move.no_manage_scope_right"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.NoSuchProjectException":
                        alert(messageResource.getMessage("task.error.move.project_not_exists"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.project.ProjectCannotBeModifiedException":
                        alert(messageResource.getMessage("task.error.move.project_not_available"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.NoSuchTaskException":
                        dispatch(removeTasks([task.id]))
                        alert(messageResource.getMessage("task.error.move.task_deleted"))
                        return true
                    case "com.actimind.actitime.task.TaskIsNotAssignedToUserException":
                        alert(messageResource.getMessage("task.error.move.task_not_assigned"))
                        window.location.reload()
                        return true
                    case "com.actimind.actitime.task.TaskExistsException":
                        alert(messageResource.getMessage("task.error.task_already_exists"))
                        return true
                    default:
                        return handleCommonTaskUpdateErrors(task, exception, dispatch)
                }
            }
        }
    ).then((updatedTask) => {
        dispatch(removeUpdatingTask(taskId))
        if (task.workflowStatusId !== workflowStatusId) {
            dispatch(changedTaskWorkflowStatus({
                taskId: task.id,
                workflowStatusId: workflowStatusId,
                prevWorkflowStatusId: task.workflowStatusId
            }))
        }

        SyncCallbacks.afterUpdateTaskStatusAndPositionCb(taskId, projectId, workflowStatus)

        if (task.projectId !== targetProject.id) {
            dispatch(EntitiesActions.addTask(updatedTask))
            dispatch(toast(messageResource.getMessage('move.task.toast', [task.name, targetProject.name])))
        }

        if (updatedTask.name !== task.name)
            dispatch(toast(messageResource.getMessage('move.task.duplicate_name.toast')))

        if (forceReopen) {
            dispatch(EntitiesActions.addProject({...targetProject, archived: false}))
            dispatch(EntitiesActions.addCustomer({...targetCustomer, archived: false}))
            dispatch(toast(messageResource.getMessage("status.project.toast", [targetProject.name])))
        }

    }).catch((error) => {
        if (error.isHandled())
            return
        dispatch(removeUpdatingTask(taskId))
        if (getTask(getState(), taskId)) {
            dispatch(setTasksOrder(changeItemPosition(getTaskIdsOrder(state), taskId, currentPrecedingTaskId)))
            dispatch(EntitiesActions.addTask(task))
        }
    })
}

export const toggleMinimizedCards = () => (dispatch, getState) => {
    const state = getState()
    dispatch(toggleKanbanCardsView(!isMinimizedCards(state)))
    dispatch(savePageSettings())
}

export const addRecentlyCreatedTasks = (taskIds) => (dispatch, getState) => {
    dispatch(prependTasksOrder(taskIds))
    const state = getState()
    let tasksCountByWorkflowStatuses = {...getTaskCountsByWorkflowStatus(state)}

    taskIds.forEach(taskId => {
        const task = getTask(state, taskId)
        tasksCountByWorkflowStatuses[task.workflowStatusId] =
            !!tasksCountByWorkflowStatuses[task.workflowStatusId] ? tasksCountByWorkflowStatuses[task.workflowStatusId] + 1 : 1
    })
    dispatch(setTasksCountByWorkflowStatuses(tasksCountByWorkflowStatuses))
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/kanban/actions.js